import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'success'
type Size = 'sm' | 'md' | 'lg' | 'xl' | 'icon'

const variants: Record<Variant, string> = {
  // Black, not the brand green. See the note on --color-action: a solid green
  // primary button is indistinguishable from a solid green "Paid" badge, and
  // this is the button that takes money.
  primary: 'bg-action text-white hover:bg-action-hover',
  secondary: 'bg-surface text-ink ring-1 ring-border-strong hover:bg-sunken',
  ghost: 'text-ink-secondary hover:bg-sunken hover:text-ink',
  destructive: 'bg-critical text-white hover:brightness-95',
  success: 'bg-good text-white hover:brightness-95',
}

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-[15px] gap-2',
  // The POS Charge button. Deliberately the largest interactive element in
  // the product — it is pressed hundreds of times a day, often in a hurry.
  xl: 'h-14 px-6 text-base font-semibold gap-2.5',
  icon: 'h-10 w-10',
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  loading?: boolean
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', loading, asChild, children, disabled, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      ref={ref}
      // A loading button that is still clickable is how you get two sales.
      //
      // Only on a real <button>. Slot forwards every prop to whatever it wraps,
      // and `disabled` on an <a> is not a valid attribute — React warns, and
      // more importantly the link stays clickable, so it would be a promise
      // the markup cannot keep.
      {...(asChild ? {} : { disabled: disabled || loading })}
      className={cn(
        'inline-flex select-none items-center justify-center rounded-lg font-medium',
        'transition-colors duration-150 ease-out',
        'disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {/*
        Slot demands EXACTLY ONE child, and `React.Children.count` counts a
        falsy expression as a child — so `{loading && <Loader2/>}{children}` is
        always two, and asChild threw whether or not it was loading.

        The spinner is dropped in asChild mode rather than made conditional:
        with asChild the caller owns the markup, and injecting an icon into
        someone else's element is not ours to do.
      */}
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          {children}
        </>
      )}
    </Comp>
  )
})
